"use client";

import { CandlestickChart } from "@/components/charts/candlestick-chart";
import { Candlestick } from "@/components/charts/candlestick";
import { Grid } from "@/components/charts/grid";
import { XAxis } from "@/components/charts/x-axis";
import { YAxis } from "@/components/charts/y-axis";
import { ChartTooltip } from "@/components/charts/tooltip";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  ChartAxisFrame,
  ChartXAxisRow,
} from "@/components/charts/chart-axis-frame";
import {
  ChartBrush,
  ChartBrushLayout,
} from "@/components/charts/chart-brush";
import type { ChartBrushLayoutRenderProps } from "@/components/charts/chart-brush";
import { ChartLegendHoverProvider } from "@/components/charts/chart-legend-hover";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
} from "@/components/charts/legend";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { ModuleChartCandlestickData } from "@/types/modules";
import { chartGrayscaleStyle } from "../imports-token-scope";
import { JSX, useMemo, useState } from "react";
import {
  moduleAxisLabels,
} from "./chart-module-metrics";
import { CHART_PLOT_HEIGHT, MIN_CHART_WIDTH, MIN_PX_PER_POINT } from "@/config/layout";

const DEFAULT_DATA: ModuleChartCandlestickData = {
  points: [
    { date: "2025-01-01", open: 100, high: 108, low: 96, close: 104 },
    { date: "2025-01-02", open: 104, high: 112, low: 101, close: 109 },
    { date: "2025-01-03", open: 109, high: 111, low: 102, close: 103 },
    { date: "2025-01-04", open: 103, high: 107, low: 99, close: 106 },
    { date: "2025-01-05", open: 106, high: 114, low: 105, close: 112 },
    { date: "2025-01-06", open: 112, high: 115, low: 108, close: 110 },
    { date: "2025-01-07", open: 110, high: 118, low: 109, close: 116 },
  ],
};

const CHART_HEIGHT = CHART_PLOT_HEIGHT.candlestick;
const PX_PER_POINT = MIN_PX_PER_POINT.candlestick;

export default function ModuleCandlestickChart({
  data = DEFAULT_DATA,
  xAxisLabel,
  yAxisLabel,
  showBrush = false,
  grayscale,
  className,
}: {
  data?: ModuleChartCandlestickData;
  xAxisLabel?: string;
  yAxisLabel?: string;
  showBrush?: boolean;
  grayscale?: boolean;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const points = useMemo(
    () =>
      data.points.map((p) => ({
        ...p,
        date: new Date(p.date),
      })),
    [data.points],
  );

  const positiveColor = data.positiveColor ?? "var(--chart-4)";
  const negativeColor = data.negativeColor ?? "var(--chart-5)";

  const legendItems = useMemo(
    () => [
      { label: t("Up (close ≥ open)"), value: 0, color: positiveColor },
      { label: t("Down (close < open)"), value: 0, color: negativeColor },
    ],
    [positiveColor, negativeColor, t],
  );

  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const {
    activeIndex,
    handleSelect,
    setLabelRef,
    selectedOrigin,
    legendSelectedIndex,
    elementIndex,
    toggleElementIndex,
  } = useLegendClickSync();

  return (
    <ChartLegendHoverProvider
      hoveredIndex={legendHovered}
      onHoverChange={setLegendHovered}
      selectedIndex={activeIndex}
      selectedOrigin={selectedOrigin}
      elementIndex={elementIndex}
      onElementToggle={toggleElementIndex}
    >
    <ChartBrushLayout
      data={points as unknown as Record<string, unknown>[]}
      xDataKey="date"
    >
      {(layout: ChartBrushLayoutRenderProps<Record<string, unknown>>) => (
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartGrayscaleStyle(grayscale)}>
      <ChartAxisFrame {...moduleAxisLabels({ xAxisLabel, yAxisLabel })}>
      <SwipeScroll className="min-w-0 flex-1 pb-1 [mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)]">
      <div
        style={{
          minWidth: Math.max(MIN_CHART_WIDTH, points.length * PX_PER_POINT),
        }}
      >
      <div style={{ height: CHART_HEIGHT }}>
        <CandlestickChart
          data={points}
          margin={{ top: 20, right: 24, bottom: 8, left: 0 }}
          style={{ height: "100%" }}
          xDomain={layout.xDomain}
          xDomainSlotCount={layout.xDomainSlotCount}
        >
          <Grid horizontal />
          <Candlestick
            positiveFill={positiveColor}
            negativeFill={negativeColor}
            fadedOpacity={0.25}
          />
          <XAxis />
          <YAxis />
          <ChartTooltip
            showDatePill
            rows={(point) => [
              { color: "var(--chart-1)", label: t("Open (price)"), value: Number(point.open ?? 0) },
              { color: "var(--chart-4)", label: t("High"), value: Number(point.high ?? 0) },
              { color: "var(--chart-5)", label: t("Low"), value: Number(point.low ?? 0) },
              { color: "var(--chart-3)", label: t("Close (price)"), value: Number(point.close ?? 0) },
            ]}
          />
        </CandlestickChart>
      </div>
      <ChartXAxisRow />
      </div>
      </SwipeScroll>
      </ChartAxisFrame>

      {showBrush && points.length > 0 && (
        <ChartBrush
          data={points as unknown as Record<string, unknown>[]}
          xDataKey="date"
          variant="time"
          height={36}
          onSelectionChange={layout.onBrushSelectionChange}
          initialSelection={layout.brushSelection ?? undefined}
          previewSeries={[
            { dataKey: "close", color: positiveColor },
          ]}
        />
      )}

      <SwipeScroll className="w-full pb-1" fade="auto">
        <Legend
          items={legendItems}
          hoveredIndex={legendHovered}
          onHoverChange={setLegendHovered}
          selectedIndex={legendSelectedIndex}
          onSelect={handleSelect}
          onLabelRef={setLabelRef}
          className="flex flex-row gap-6"
        >
          <LegendItem className="flex shrink-0 items-center gap-2">
            <LegendMarker className="h-2.5 w-2.5" />
            <LegendLabel />
          </LegendItem>
        </Legend>
      </SwipeScroll>
    </div>
      )}
    </ChartBrushLayout>
    </ChartLegendHoverProvider>
  );
}
